import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  Rating,
  CircularProgress,
  Divider,
  Avatar,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { getColorLabel } from '../utils/colors';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

interface ReviewItem {
  product_id: number;
  name: string;
  size?: string | null;
  color?: string | null;
  image?: string | null;
}

interface TokenResponse {
  order_id: number;
  email: string;
  created_at: string;
  items: ReviewItem[];
}

interface LocalReview {
  productId: number;
  index: number;
  rating: number;
  comment: string;
}

const ReviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [order, setOrder] = useState<TokenResponse | null>(null);
  const [authorName, setAuthorName] = useState('');
  const [reviews, setReviews] = useState<LocalReview[]>([]);

  useEffect(() => {
    if (!token) {
      setError('Lien invalide.');
      setOrder(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccess(false);
      try {
        const res = await fetch(
          `${API_BASE_URL}/review-tokens/${encodeURIComponent(token)}`,
          { signal: controller.signal }
        );

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const message = data && data.error ? data.error : 'Lien invalide ou expiré.';
          if (!controller.signal.aborted) {
            setError(message);
            setOrder(null);
            setReviews([]);
          }
          return;
        }

        const data: TokenResponse = await res.json();
        if (controller.signal.aborted) return;

        setOrder(data);
        const initialReviews: LocalReview[] = Array.isArray(data.items)
          ? data.items.map((item, index) => ({
              productId: item.product_id,
              index,
              rating: 5,
              comment: '',
            }))
          : [];
        setReviews(initialReviews);
      } catch (e) {
        if (!controller.signal.aborted) {
          setError('Erreur lors du chargement du lien.');
          setOrder(null);
          setReviews([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [token]);

  const updateReview = (
    productId: number,
    index: number,
    patch: Partial<Pick<LocalReview, 'rating' | 'comment'>>
  ) => {
    setReviews((prev) =>
      prev.map((r) =>
        r.productId === productId && r.index === index ? { ...r, ...patch } : r
      )
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || !order || reviews.length === 0) {
      return;
    }

    const valid = reviews.filter(
      (r) => Number.isFinite(r.rating) && r.rating >= 1 && r.rating <= 5
    );

    if (valid.length === 0) {
      setError('Merci de noter au moins un produit.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_BASE_URL}/review-tokens/${encodeURIComponent(token)}/reviews`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authorName: authorName.trim() || null,
            reviews: valid.map((r) => ({
              productId: r.productId,
              rating: r.rating,
              comment: r.comment,
            })),
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const message =
          data && data.error
            ? data.error
            : "Erreur lors de l'enregistrement de vos avis.";
        setError(message);
        return;
      }

      setSuccess(true);
    } catch {
      setError("Erreur réseau lors de l'enregistrement de vos avis.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Chargement du lien d'avis...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !order) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Lien d'avis invalide
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!order) {
    return null;
  }

  const disabled = submitting || success;

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Donner votre avis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Merci pour votre commande chez Bratan Family. Vous pouvez noter les produits
          ci-dessous. Ce lien est valable une seule fois.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Commande n° {order.order_id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            E-mail : {order.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date : {new Date(order.created_at).toLocaleString()}
          </Typography>
        </Box>

        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {success && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="primary">
              Merci beaucoup pour vos avis !
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ils ont bien été enregistrés. Vous pouvez maintenant fermer cette page.
            </Typography>
          </Box>
        )}

        {!success && (
          <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Votre nom ou pseudo (optionnel)"
            value={authorName}
            onChange={(event) => setAuthorName(event.target.value)}
            fullWidth
            sx={{ mb: 3 }}
            disabled={disabled}
          />

          <Divider sx={{ mb: 3 }} />

          <Stack spacing={3}>
            {order.items.map((item, index) => {
              const review = reviews.find(
                (r) => r.productId === item.product_id && r.index === index
              );
              const ratingValue = review ? review.rating : 0;
              const commentValue = review ? review.comment : '';
              const colorLabel = item.color ? getColorLabel(item.color) : undefined;

              return (
                <Box
                  key={`${item.product_id}-${index}`}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    {item.image ? (
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.name}
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1,
                          objectFit: 'cover',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Avatar sx={{ mr: 2 }}>
                        {item.name.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.size && `Taille : ${item.size}`}
                        {item.size && colorLabel && ' · '}
                        {colorLabel && `Couleur : ${colorLabel}`}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ mb: 1 }}>
                      <Rating
                        name={`rating-${item.product_id}-${index}`}
                        value={ratingValue}
                        precision={1}
                        onChange={(_, value) =>
                          updateReview(item.product_id, index, {
                            rating: value || 0,
                          })
                        }
                        disabled={disabled}
                      />
                    </Box>
                    <TextField
                      label="Votre avis (optionnel)"
                      multiline
                      minRows={2}
                      fullWidth
                      value={commentValue}
                      onChange={(event) =>
                        updateReview(item.product_id, index, {
                          comment: event.target.value,
                        })
                      }
                      disabled={disabled}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ mt: 4, textAlign: 'right' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={disabled}
            >
              {success ? 'Avis envoyés' : 'Envoyer mes avis'}
            </Button>
          </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ReviewPage;
